import { Path, useForm } from "react-hook-form";
import { Box } from "@mui/material";
import FormRow from "components/form/FormRow";
import { MESSAGE } from "constants/validate";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import CancelButton from "components/button/CancelButton";
import ButtonComponent from "components/button/ButtonComponent";
import { formatDateTime } from "utils";
import { useEffect, useMemo } from "react";
import { Client, ClientUpdatePayload } from "../types/client";

const styles = {
  field: {
    width: "100%",
    height: "48px",
    boxSizing: "border-box",
    backgroundColor: "var(--white)",
    borderRadius: 0,
    border: "3px solid var(--silver-150)",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  closeBtn: {},
};

interface IClientFormProps {
  visible: boolean;
  onClose: () => void;
  onHandleSubmit: (form: ClientUpdatePayload) => void;
  isLoading: boolean;
  clientDetail: Client;
}

/**
 * ClientFormModal Component
 *
 * This component renders a modal form for updating client details.
 * It uses `react-hook-form` for form management and validation.
 *
 * @param {IClientFormProps} props - The props for the component.
 * @param {boolean} props.visible - Controls the visibility of the modal.
 * @param {() => void} props.onClose - Callback function to close the modal.
 * @param {(form: ClientUpdatePayload) => void} props.onHandleSubmit - Callback function to handle form submission.
 * @param {boolean} props.isLoading - Indicates whether the form is in a loading state.
 * @param {Client} props.clientDetail - Client details for pre-filling the form fields.
 */
export default function ClientFormModal({
  visible,
  onClose,
  onHandleSubmit,
  isLoading,
  clientDetail,
}: IClientFormProps) {
  const useClientFormValues = useMemo(
    () => ({
      store_cd: clientDetail.store_cd,
      store_name: clientDetail.store_name,
      client_cd: clientDetail.client_cd,
      company_cd: clientDetail.company_cd,
      company_name: clientDetail.company_name,
      postal_cd: clientDetail.postal_cd,
      address: clientDetail.address,
      tel_no: clientDetail.tel_no,
      fax_no: clientDetail.fax_no,
      kbn4: clientDetail.kbn4,
      kbn5: clientDetail.kbn5,
      managed_logistics_cd: clientDetail.managed_logistics_cd,
      client_name: clientDetail.mst_client_client_name,
      status_flag: clientDetail.mst_client_status_flag || 1,
    }),
    [clientDetail],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ClientUpdatePayload>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useClientFormValues,
  });

  const onSubmit = (data: ClientUpdatePayload) => {
    if (isLoading || Object.keys(errors).length > 0) return;
    onHandleSubmit(data);
  };

  const clientFormFields = [
    { label: "店舗コード", disabled: true, fieldName: "store_cd" },
    { label: "店舗名", disabled: true, fieldName: "store_name" },
    { label: "得意先コード", disabled: true, fieldName: "client_cd" },
    { label: "企業コード", disabled: true, fieldName: "company_cd" },
    { label: "企業名", disabled: true, fieldName: "company_name" },
    { label: "郵便番号", disabled: true, fieldName: "postal_cd" },
    { label: "住所", disabled: true, fieldName: "address" },
    { label: "電話番号", disabled: true, fieldName: "tel_no" },
    { label: "FAX番号", fieldName: "fax_no", disabled: true },
    // { label: "区分4", fieldName: "kbn4", disabled: true },
    // { label: "区分5", fieldName: "kbn5", disabled: true },
    {
      label: "管轄物流コード",
      type: "text",
      disabled: true,
      fieldName: "managed_logistics_cd",
    },
    {
      label: "得意先名称",
      fieldName: "client_name",
      rules: {
        required: MESSAGE.EMPTY,
        maxLength: { value: 100, message: MESSAGE.MAX_LENGTH(100) },
      },
    },
    { label: "有効/無効", type: "select", fieldName: "status_flag" },
  ];

  useEffect(() => {
    if (clientDetail && clientDetail.id) {
      reset(useClientFormValues);
    }
  }, [clientDetail?.id]);

  return (
    <Modal
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      open={visible}
      onClose={onClose}
    >
      <Box
        sx={{
          maxHeight: "90vh",
          overflow: "auto",
          width: "80%",
          maxWidth: "870px",
          backgroundColor: "var(--white)",
          padding: { xs: 3, md: 4, xl: "30px 70px" },
          borderRadius: "21px",
          display: "flex",
          flexDirection: "column",
          rowGap: "20px",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="10px">
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: "fontWeightMedium",
              color: "var(--gray-700)",
              fontSize: { xs: 28, md: 33 },
            }}
          >
            得意先詳細
            <Typography component="br" sx={{ display: { xs: "block", md: "none" } }} />
            <Typography
              component="span"
              sx={{
                fontSize: { xs: 16, md: 21 },
                fontWeight: "fontWeightMedium",
                color: "var(--gray-700)",
                ml: { xs: 0, md: 3 },
              }}
            >
              最終連携日 {formatDateTime(clientDetail.updated_at)}
            </Typography>
          </Typography>
          <CancelButton onClose={onClose} />
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          {clientFormFields.map((el) => (
            <FormRow
              key={el.fieldName}
              sxLabel={{ pl: 4, width: "40%" }}
              label={el.label}
              sxBoxField={{ width: "60%", flexGrow: 1 }}
              sxField={{ ...styles.field }}
              name={el.fieldName as Path<ClientUpdatePayload>}
              type={el.type as never}
              control={control}
              rules={el.rules ? el.rules : undefined}
              options={
                el.type === "select"
                  ? [
                      { label: "有効", value: 1 },
                      { label: "無効", value: 0 },
                    ]
                  : undefined
              }
              disabled={el.disabled}
            />
          ))}

          <Box display="flex" flexDirection="column" rowGap="15px" marginTop="25px">
            <ButtonComponent
              disabled={isLoading || Object.keys(errors).length > 0}
              type="submit"
              label="更新"
              theme="dark"
              sx={{ height: "50px", minWidth: "185px", width: "28%" }}
            />
            <Typography
              sx={{ fontSize: { xs: 16, md: 21 } }}
              fontWeight="medium"
              color="var(--gray-600)"
            >
              更新日 {formatDateTime(clientDetail?.mst_client_updated_at || "")}
            </Typography>
          </Box>
        </form>
        <CancelButton sx={{ mt: 1.5 }} onClose={onClose} />
      </Box>
    </Modal>
  );
}
